/**
 * Atoms shared by the metric dashboards (CreativeDashboard,
 * PerformanceDashboard) so the same platform renders the same color and the
 * chart skeletons stay pixel-consistent across adjacent dashboards.
 */
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import {
  CartesianGrid,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { BRAND_COLORS } from '@constants/shared-colors';
import { Platform, PLATFORM_TITLE } from '@enums/Platform';
import { normalizePlatform } from '@utils/platform';
import {
  Activity,
  Gallery,
  Notepad,
  Routing2,
  SearchNormal1,
  Settings,
  StatusUp,
} from 'iconsax-react';

/** The one list behind every "is this a dashboard?" check (tab layout, chips,
 * icons). Add new dashboards HERE — consumers derive from it, and the glyph
 * map below fails to compile until the new entry gets an icon. */
export const DASHBOARD_ENTITY_TYPES = [
  'creativesDashboard',
  'analyticsDashboard',
  'leadAttribution',
  'galleryDashboard',
  'campaignTimeline',
  'monitorsDashboard',
] as const;
export type DashboardEntityType = (typeof DASHBOARD_ENTITY_TYPES)[number];

/** The Analytics dashboard's former entity type and id, from when the page was Performance. Every chip,
 * tab and link saved before the rename carries them; they are read as the Analytics dashboard, never
 * written. */
export const LEGACY_ANALYTICS_TYPE = 'performanceDashboard';
export const LEGACY_ANALYTICS_TAB_KEY = 'performanceDashboard-performance';
export const ANALYTICS_TAB_KEY = 'analyticsDashboard-analytics';

/** An entity type as it is now: the Analytics dashboard's former type reads as its current one. */
export const canonicalEntityType = <T extends string | undefined>(entityType: T): T =>
  (entityType === LEGACY_ANALYTICS_TYPE ? 'analyticsDashboard' : entityType) as T;

/** A tab key as it is now: the Analytics dashboard's former key (saved tabs, saved Home layouts) reads
 * as its current one. */
export const canonicalTabKey = (key: string): string =>
  key === LEGACY_ANALYTICS_TAB_KEY ? ANALYTICS_TAB_KEY : key;

/** The `dashboard` value an analytics event carries. The Analytics dashboard reports under its former
 * type, so its trends run unbroken across the rename from the Performance tab. */
export const eventDashboard = (dashboard: string | null): string | null =>
  dashboard === 'analyticsDashboard' ? LEGACY_ANALYTICS_TYPE : dashboard;

/** Narrow an arbitrary entity-type string to a dashboard type, or null. */
export const asDashboardEntityType = (
  entityType: string | undefined,
): DashboardEntityType | null => {
  const type = canonicalEntityType(entityType);
  return type && (DASHBOARD_ENTITY_TYPES as readonly string[]).includes(type)
    ? (type as DashboardEntityType)
    : null;
};

/**
 * One ARTIFACT name per dashboard, defined once: what a chip in chat says,
 * whether dragged from the tab, dragged from the sidebar, or handed back by Leo.
 * The self-describing form wins there because "Analytics" sitting beside
 * campaign and asset chips does not say what it is. It fits the chip's 25-char
 * truncation, and `test_dashboard_artifacts.py` pins the backend's chip to the
 * same string.
 *
 * The tab itself shows `label`, which may be shorter — `BrowserTabs` renders
 * `tab.label` but drags `tab.entityName`, so a tab drag and a sidebar drag
 * still produce the identical chip.
 */
export const DASHBOARD_NAMES = {
  creatives: 'Creatives dashboard',
  analytics: 'Analytics dashboard',
  leadAttribution: 'Lead attribution',
  gallery: 'Gallery',
  monitors: 'Monitors',
  campaignTimeline: 'Campaign Overview',
} as const;

const DASHBOARD_CHIP_NAME = {
  creativesDashboard: DASHBOARD_NAMES.creatives,
  analyticsDashboard: DASHBOARD_NAMES.analytics,
  leadAttribution: DASHBOARD_NAMES.leadAttribution,
  galleryDashboard: DASHBOARD_NAMES.gallery,
  campaignTimeline: DASHBOARD_NAMES.campaignTimeline,
  monitorsDashboard: DASHBOARD_NAMES.monitors,
} satisfies Record<DashboardEntityType, string>;

/** The name an `<artifact>` chip shows. A dashboard is one place with one name, so its chip reads
 * that name whatever the tag wrote — none, or an old one like the legacy `performanceDashboard`'s
 * "Performance dashboard" — as the backend's `name_page_chips` stamps it. */
export const artifactChipName = (type: string | undefined, name: string | undefined): string => {
  const dashboard = asDashboardEntityType(type);
  return dashboard ? DASHBOARD_CHIP_NAME[dashboard] : name || 'Untitled';
};

/** Short tab-strip label per dashboard. Chips and drags carry the longer
 * self-describing artifact name (DASHBOARD_NAMES); addTab normalizes any
 * dashboard tab back to these so no tab ever reads "… dashboard". */
export const DASHBOARD_TAB_LABEL = {
  creativesDashboard: 'Creatives',
  analyticsDashboard: 'Analytics',
  leadAttribution: 'Lead attribution',
  galleryDashboard: 'Gallery',
  campaignTimeline: 'Campaign Overview',
  monitorsDashboard: 'Monitors',
} satisfies Record<DashboardEntityType, string>;

const DASHBOARD_GLYPH = {
  creativesDashboard: Notepad,
  analyticsDashboard: StatusUp,
  leadAttribution: Routing2,
  galleryDashboard: Gallery,
  campaignTimeline: Settings,
  monitorsDashboard: Activity,
} satisfies Record<DashboardEntityType, typeof Notepad>;

/** The canonical glyph for a dashboard wherever it appears in navigation or Home. */
export const DashboardIcon = ({
  entityType,
  size = 16,
  variant = 'Linear',
}: {
  entityType: DashboardEntityType;
  size?: number;
  variant?: 'Linear' | 'Outline';
}) => {
  const Glyph = DASHBOARD_GLYPH[entityType];
  return <Glyph size={size} variant={variant} color="currentColor" />;
};

/** Metric tile rows (Summary KPIs, GA4 signals): every track is 1fr so tiles
 * stay equal width, and the minmax floor — sized to the longest nowrap label —
 * is what makes the row wrap instead of squeezing tiles. */
export const METRIC_TILE_GRID =
  'grid grid-cols-[repeat(auto-fit,minmax(12rem,1fr))] gap-3';

/** The white-card chrome every dashboard tile and chart card sits in. */
export const DASHBOARD_CARD = 'rounded-xl border border-light-100 bg-white p-4';

/** A row of metric tiles in ONE card with hairline dividers between them — the
 *  Monitors/Performance look, preferred over separate per-metric DASHBOARD_CARDs.
 *  Put TILE_GROUP_CARD on the card, TILE_GROUP_GRID on the grid inside it, and
 *  TILE_CELL on each tile. The card's overflow-hidden plus the grid's negative
 *  margins clip the outermost cell borders, so only the interior rules show, and
 *  the grid stays in step with METRIC_TILE_GRID's 12rem wrap floor. */
export const TILE_GROUP_CARD =
  'overflow-hidden rounded-xl border border-light-100 bg-white';
export const TILE_GROUP_GRID =
  'grid -mb-px -mr-px auto-rows-fr grid-cols-[repeat(auto-fit,minmax(12rem,1fr))]';
/** One tile inside a TILE_GROUP_GRID: its own bottom/right rule becomes the
 *  divider shared with its neighbours. Callers add their own content layout. */
export const TILE_CELL = 'border-b border-r border-light-100 p-4';

/** Fills the space below the sticky header so a full-page empty state centers in view. */
export const FULL_PAGE_EMPTY = 'min-h-[calc(100vh-240px)]';
/** Flush in a tile's top-right corner. No halo, and kept just inside the tile
 *  bounds (top-0/right-0, not straddling the edge) so the group card's
 *  overflow-hidden doesn't clip the top row. The tile needs `relative`. */
export const CARD_CORNER_NOTICE = 'absolute right-0 top-0';
/** A metric tile's uppercase label row. */
export const METRIC_TILE_LABEL =
  'whitespace-nowrap text-[11px] font-medium uppercase tracking-wide text-leo-secondary';
/** A metric tile's headline value. */
export const METRIC_TILE_VALUE = 'text-xl font-semibold';
/** Section headers outside their cards; callers add their own margins. */
export const SECTION_TITLE = 'm-0 text-base font-semibold text-body';
/** The quiet gray inset for card footnotes and evidence caveats; callers add
 *  their own margins. */
const FOOTNOTE = 'rounded-lg px-2 py-1 text-xs leading-5 text-leo-secondary';
export const DASHBOARD_FOOTNOTE = `${FOOTNOTE} bg-[var(--color-light400)]`;
/** Inside a white card or drawer, one shade lighter keeps the inset quiet. */
export const DASHBOARD_FOOTNOTE_ON_WHITE = `${FOOTNOTE} bg-[var(--color-light300)]`;

/** A footnote inset that collapses to a single truncated line, with a
 *  more…/less toggle. `className` supplies the inset background + margins
 *  (e.g. DASHBOARD_FOOTNOTE_ON_WHITE). */
export const CollapsibleFootnote = ({
  children,
  className = '',
  defaultExpanded = false,
}: {
  children: ReactNode;
  className?: string;
  defaultExpanded?: boolean;
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <div className={`flex items-start gap-2 ${className}`}>
      <p className={`m-0 min-w-0 flex-1 ${expanded ? '' : 'truncate'}`}>
        {children}
      </p>
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="shrink-0 px-2 font-medium text-leo-secondary transition-colors hover:text-body"
      >
        {expanded ? 'less' : 'more…'}
      </button>
    </div>
  );
};

/** A dashboard section: title (and optional subtitle/extra) OUTSIDE the white
 * box, styled like the creatives dashboard's section headers. The outer flex +
 * inner flex-1 keep side-by-side boxes equal height when a grid row stretches
 * its cells; the box is flex-col so children can center themselves with
 * my-auto. */
export const Card = ({
  title,
  subtitle,
  children,
  extra,
  className = '',
  flat = false,
}: {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  extra?: ReactNode;
  className?: string;
  /** Skip the white box. A section whose only content is a table sits directly
   *  on the page, like the Creatives dashboard's All Creatives table. */
  flat?: boolean;
}) => (
  <section className={`flex min-w-0 flex-col ${className}`}>
    {(title || extra) && (
      <div className="mb-4 flex items-start justify-between gap-3">
        {title && (
          <div>
            <h2 className={SECTION_TITLE}>{title}</h2>
            {subtitle && (
              <p className="m-0 mt-1 text-xs leading-5 text-leo-secondary">
                {subtitle}
              </p>
            )}
          </div>
        )}
        {extra}
      </div>
    )}
    {flat ? (
      children
    ) : (
      <div className={`flex min-h-0 min-w-0 flex-1 flex-col ${DASHBOARD_CARD}`}>
        {children}
      </div>
    )}
  </section>
);

/** Ant Table `loading` for dashboard tables: the app's ring instead of Ant's
 *  dot spinner; .dashboard-table in ant-global.scss paints it text-colored. */
export const tableLoading = (spinning: boolean) => ({
  spinning,
  indicator: <LoadingOutlined spin />,
});

/** A pulsing placeholder bar sized by the caller — the one skeleton atom the
 *  dashboards share. Loading states are built from these inside the REAL
 *  layout (card chrome, section titles, tile grids stay rendered), so the
 *  skeleton previews the exact shape of the screen about to fill in and
 *  nothing jumps when it does. */
export const Pulse = ({
  className,
  style,
}: {
  className: string;
  style?: CSSProperties;
}) => (
  // The app's neutral gray, not Tailwind's gray-200 — that one is blue-hued
  // and reads as off-palette next to the app's zero-saturation lights.
  <span
    className={`inline-block animate-pulse rounded bg-[var(--color-light400)] ${className}`}
    style={style}
  />
);

/** The loading twin of the TILE_GROUP atoms: one card of hairline-divided cells,
 *  each a label + value pulse. The tile-grid every metric row fills into, before
 *  its values land. Caller renders the real section heading above it. */
export const TileGridSkeleton = ({ count = 4 }: { count?: number }) => (
  <div className={TILE_GROUP_CARD}>
    <div className={TILE_GROUP_GRID}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className={`flex flex-col gap-1 ${TILE_CELL}`}>
          <Pulse className="h-3 w-16" />
          <Pulse className="h-6 w-20" />
        </div>
      ))}
    </div>
  </div>
);

export const isCurrency = (
  currency: string | null | undefined,
): currency is string => Boolean(currency && /^[A-Z]{3}$/.test(currency));

/** Money for dashboard surfaces: '—' for null/unverified currency, compact
 *  notation from 1000 up unless `compact` is false. */
export const fmtMoney = (
  value: number | null | undefined,
  currency: string | null | undefined,
  compact = true,
) => {
  if (value === null || value === undefined || !isCurrency(currency))
    return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    notation: compact && Math.abs(value) >= 1000 ? 'compact' : 'standard',
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);
};

/** The up/down trend chip beside a KPI value. `invert` marks lower-is-better
 *  metrics. Default renders an em-dash for a null delta; `hideEmpty` renders
 *  nothing for null or zero. */
export const Delta = ({
  pct,
  invert = false,
  hideEmpty = false,
}: {
  pct: number | null;
  invert?: boolean;
  hideEmpty?: boolean;
}) => {
  if (pct === null || !Number.isFinite(pct) || (hideEmpty && pct === 0))
    return hideEmpty ? null : (
      <span className="text-xs text-leo-secondary">—</span>
    );
  const good = invert ? pct < 0 : pct > 0;
  return (
    <span
      className={`text-xs font-medium ${good ? 'text-green-600' : 'text-red-500'}`}
    >
      {pct > 0 ? '▲' : '▼'} {Math.abs(pct)}%
    </span>
  );
};

// Chart series colors, deliberately chart-tuned rather than brand-exact (cf.
// BrandBadge's FALLBACK_BG): Meta reuses the app's primary blue, and LinkedIn
// is a deep navy — its brand mid-blue read too close to Meta's sky blue.
// recharts can't read CSS vars, so these are literals. Platforms without a
// tuned hue fall back to purple.
export const PLATFORM_CHART_COLOR: Partial<Record<Platform, string>> = {
  [Platform.META]: BRAND_COLORS.primary,
  [Platform.GOOGLE]: '#EA4335',
  [Platform.TIKTOK]: '#111827',
  [Platform.LINKEDIN]: '#0B2A5B',
  [Platform.REDDIT]: '#FF4500',
  [Platform.AMAZON]: '#FF9900',
  [Platform.BING]: '#0E7A6E',
  [Platform.X]: '#4A4E5A',
};

/** Label + chart color for any platform spelling a payload may carry. */
export const platformStyle = (p: string) => {
  const platform = normalizePlatform(p);
  return {
    label: PLATFORM_TITLE[platform],
    color: PLATFORM_CHART_COLOR[platform] ?? BRAND_COLORS.purple,
  };
};

/** Colored square + label — the legend/platform-chip atom used everywhere. */
export const Swatch = ({ color, label }: { color: string; label: string }) => (
  <span className="flex items-center gap-1.5 min-w-0">
    <span
      className="inline-block h-2.5 w-2.5 rounded-sm flex-shrink-0"
      style={{ backgroundColor: color }}
    />
    <span className="truncate">{label}</span>
  </span>
);

// The empty-state chart frame: recharts won't derive gridlines or ticks from
// an empty dataset, so both are given explicitly — gridline positions computed
// from the chart geometry to line up with the [0,100] tick labels.
export const EMPTY_AXIS = {
  domain: [0, 100] as [number, number],
  ticks: [0, 20, 40, 60, 80, 100],
};
/** Percent metrics are fractions their formatter multiplies by 100, so the
 *  raw [0,100] frame would label the top tick "10000.00%". Shrink to [0,1]. */
export const emptyAxisFor = (percent: boolean) =>
  percent
    ? {
        domain: [0, 1] as [number, number],
        ticks: EMPTY_AXIS.ticks.map((t) => t / 100),
      }
    : EMPTY_AXIS;
export const EMPTY_X_AXIS_HEIGHT = 20;

// Recharts 2 reserves a fixed 60px band for every YAxis regardless of what the
// labels need, which leaves a dead gutter beside short ones ("$4"). Measure
// the formatted labels and size the axis to the widest instead.
let measureCtx: CanvasRenderingContext2D | null = null;
let bodyFontFamily: string | null = null;
export const yAxisWidth = (labels: string[], fontSize = 11): number => {
  measureCtx ??= document.createElement('canvas').getContext('2d');
  bodyFontFamily ??= getComputedStyle(document.body).fontFamily;
  const ctx = measureCtx;
  if (!ctx) return 60;
  ctx.font = `${fontSize}px ${bodyFontFamily}`;
  const widest = Math.max(...labels.map((label) => ctx.measureText(label).width));
  return Math.max(30, Math.ceil(widest) + 10);
};

/** Axis width for a [0, max] domain: measures the data max AND the top "nice"
 *  tick recharts may grow to — forget the latter and long labels clip. */
export const yAxisWidthFor = (
  max: number,
  fmt: (v: number) => string,
): number => yAxisWidth([fmt(max), fmt(niceCeil(max))]);

/** The top "nice" tick recharts will pick for a [0, max] domain — the widest
 *  label the axis can grow to, so it gets measured alongside the data max. */
export const niceCeil = (max: number): number => {
  if (max <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(max));
  return ([1, 2, 2.5, 5, 10].find((factor) => factor * pow >= max) ?? 10) * pow;
};

/** Gridline y-positions matching EMPTY_AXIS's ticks for a chart's geometry. */
export const emptyGridPoints = (
  height: number,
  margin: { top: number; bottom: number },
) =>
  EMPTY_AXIS.ticks.map((_, i, ticks) => {
    const top = margin.top;
    const bottom = height - margin.bottom - EMPTY_X_AXIS_HEIGHT;
    return top + ((bottom - top) * i) / (ticks.length - 1);
  });

/**
 * The empty chart frame: y-axis scale and gridlines with a centered message,
 * so a loading chart reads as a chart waiting for data.
 */
export const EmptyChartFrame = ({
  isLoading,
  emptyMessage,
  tickFormatter,
  height = 280,
  margin = { top: 8, right: 8, left: 8, bottom: 8 },
}: {
  isLoading: boolean;
  emptyMessage: string;
  tickFormatter: (v: number) => string;
  height?: number;
  margin?: { top: number; right: number; left: number; bottom: number };
}) => {
  const axisWidth = yAxisWidth(EMPTY_AXIS.ticks.map(tickFormatter));
  if (isLoading) {
    return <Pulse className="block w-full rounded-xl" style={{ height }} />;
  }
  return (
    <div className="relative" style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={[]} margin={margin}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e5e7eb"
            vertical={false}
            horizontalPoints={emptyGridPoints(height, margin)}
          />
          <XAxis
            dataKey="day"
            height={EMPTY_X_AXIS_HEIGHT}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
            tick={false}
          />
          <YAxis
            domain={EMPTY_AXIS.domain}
            ticks={EMPTY_AXIS.ticks}
            width={axisWidth}
            fontSize={11}
            tickLine={false}
            axisLine={false}
            tickFormatter={tickFormatter}
          />
        </LineChart>
      </ResponsiveContainer>
      <div
        className="absolute inset-y-0 right-0 flex items-center justify-center px-4"
        style={{ left: margin.left + axisWidth }}
      >
        {/* A loading ring, not "Loading…" text — the same ring the table overlay
         *  and the Creatives chart use, so every surface reads one way. A white
         *  backing cuts it cleanly out of the gridlines behind it. */}
        {isLoading ? (
          <span className="bg-white px-10 py-6">
            <LoadingOutlined spin className="text-leo-secondary" />
          </span>
        ) : (
          <span className="max-w-sm bg-white px-2 text-center text-sm text-leo-secondary">
            {emptyMessage}
          </span>
        )}
      </div>
    </div>
  );
};

/** The "Add columns / Add tiles" palette body shared by the metric dashboards'
 *  settings pickers: a search box over a filtered list, so the search input, the
 *  case-insensitive label filter, and the empty state stay identical everywhere.
 *  The caller owns each row's markup (checkbox, disabled rule, label) via
 *  `renderItem` and supplies the text to match on via `labelOf`. Query state is
 *  local and resets each time the palette mounts (i.e. each time "Add" is opened). */
export function SearchablePalette<T>({
  items,
  labelOf,
  renderItem,
  placeholder = 'Search',
}: {
  items: T[];
  labelOf: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  placeholder?: string;
}) {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const hits = needle
    ? items.filter((item) => labelOf(item).toLowerCase().includes(needle))
    : items;
  return (
    <div className="flex flex-col gap-1">
      <Input
        size="small"
        allowClear
        autoFocus
        placeholder={placeholder}
        prefix={<SearchNormal1 size={13} color="currentColor" />}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {hits.length ? (
        hits.map((item) => renderItem(item))
      ) : (
        <span className="py-1 text-sm text-leo-secondary">No matches</span>
      )}
    </div>
  );
}
